/**
 * licito.guru — styled-components / MUI theme
 *
 * The product stack is React + TypeScript + MUI v6 + styled-components +
 * Emotion. This file is the canonical theme object for both libraries.
 *
 * INTEGRATION (TS):
 *
 *   // src/theme/theme.ts
 *   import { createTheme, ThemeOptions } from '@mui/material/styles';
 *   import { theme as designSystemTheme } from './design-system-theme';
 *
 *   export const muiTheme = createTheme({ ...designSystemTheme.mui });
 *
 *   // src/index.tsx — wrap once at the root
 *   <MuiThemeProvider theme={muiTheme}>
 *     <ScThemeProvider theme={designSystemTheme}>
 *       <App />
 *     </ScThemeProvider>
 *   </MuiThemeProvider>
 *
 * MIGRATION FROM `getCurrentColors()` / `getCurrentImages()`:
 *
 *   The current `src/utils/colors.ts` returns a flat object based on
 *   subdomain. Replace those `COLORS.primaria` / `COLORS.azul` calls
 *   with theme tokens via `props.theme.brand.navy` etc, and use the
 *   tenant resolver below at render-time.
 */

import {DefaultTheme} from 'styled-components'
import {getSubdomain} from '../utils/colors'

// ============================================================================
// Tenant detection — same logic as src/utils/colors.ts:getSubdomain()
// ============================================================================

export type TenantId =
    | 'default'
    | 'florianopolis'
    | 'finep'
    | 'procon'
    | 'badesul'
    | 'tre-mt'
    | 'palmas'
    | 'pcsp'
    | 'crea-sc'
    | 'paulodefrontin'
    | 'palhoca'
    | 'dpmt'
    | 'cinderondonia'

export function detectTenant(): TenantId {
    if (typeof window === 'undefined') return 'default'
    const sub = getSubdomain() as TenantId | null
    return sub && TENANT_BRAND_OVERRIDES[sub] ? sub : 'default'
}

// ============================================================================
// Default brand (licito.guru)
// ============================================================================

const defaultBrand = {
    navy: '#012441',
    navySoft: '#053859',
    mint: '#3FFE81',
    blue: '#3394EF',
    teal: '#23DBBC',
    blueSoft: '#9CE3FD',
    blueMid: '#35A6DD',
    tealSoft: '#3BD8A9',

    gradients: {
        appIcon: 'linear-gradient(135deg, #3FFE81 0%, #3394EF 100%)',
        mark: 'linear-gradient(135deg, #9CE3FD 0%, #3EFF82 100%)',
        headline: 'linear-gradient(90deg, #35A6DD 0%, #3BD8A9 100%)',
        loginBtn: 'linear-gradient(90deg, #3FFE81 0%, #3394EF 100%)',
    },
}

// ============================================================================
// Tenant overrides — only what differs from default brand
// Mirror of src/utils/colors.ts SUBDOMAIN_THEMES
// ============================================================================

type BrandOverride = Omit<Partial<typeof defaultBrand>, 'gradients'> & {
    gradients?: Partial<typeof defaultBrand.gradients>
}

const TENANT_BRAND_OVERRIDES: Record<TenantId, BrandOverride> = {
    default: {},
    florianopolis: {
        blue: '#01BAF2',
        navy: '#050B29',
        gradients: {
            appIcon: 'linear-gradient(135deg, #01BAF2 0%, #050B29 100%)',
            loginBtn: '#01BAF2',
        },
    },
    finep: {
        blue: '#F16523',
        navy: '#016361',
        gradients: {
            appIcon: 'linear-gradient(135deg, #F16523 0%, #016361 100%)',
            loginBtn: '#F16523',
        },
    },
    procon: {blue: '#014E9B', gradients: {loginBtn: '#014E9B'}},
    badesul: {blue: '#08491F', gradients: {loginBtn: '#08491F'}},
    'tre-mt': {blue: '#2532B2', gradients: {loginBtn: '#2532B2'}},
    palmas: {blue: '#002266', gradients: {loginBtn: '#002266'}},
    pcsp: {blue: '#B5A75A', gradients: {loginBtn: '#B5A75A'}},
    'crea-sc': {blue: '#004C93', gradients: {loginBtn: '#004C93'}},
    paulodefrontin: {blue: '#2389CA'},
    palhoca: {blue: '#2389CA'},
    dpmt: {blue: '#176038', gradients: {loginBtn: '#176038'}},
    cinderondonia: {blue: '#1E4721', gradients: {loginBtn: '#1E4721'}},
}

function resolveBrand(tenant: TenantId) {
    const override = TENANT_BRAND_OVERRIDES[tenant] || {}
    return {
        ...defaultBrand,
        ...override,
        gradients: {
            ...defaultBrand.gradients,
            ...(override.gradients || {}),
        },
    }
}

// ============================================================================
// Shared tokens — surface, text, border, feedback, typography, spacing
// These do NOT change per tenant.
// ============================================================================

const surface = {
    page: '#F6F8FA',
    card: '#FFFFFF',
    raised: '#FFFFFF',
    sunken: '#F0F3F6',
    overlay: 'rgba(1, 36, 65, 0.45)',
    sidebar: '#FFFFFF',
    sidebarActive: '#ECF4FA',
    sidebarHover: '#F4F8FB',
    input: '#FFFFFF',
    disabled: '#F0F0F0',
}

const text = {
    primary: '#012441',
    secondary: '#526A7E',
    tertiary: '#8A9AA8',
    muted: '#A8B4BF',
    inverse: '#FFFFFF',
    link: '#3394EF',
    onBrand: '#FFFFFF',
}

const border = {
    subtle: 'rgba(1, 36, 65, 0.08)',
    default: 'rgba(1, 36, 65, 0.12)',
    strong: 'rgba(1, 36, 65, 0.20)',
    focus: '#3394EF',
}

const feedback = {
    success: {default: '#1D9E75', soft: '#E1F5EE', strong: '#0F6E56'},
    warning: {default: '#D97706', soft: '#FEF3C7', strong: '#92400E'},
    danger: {default: '#B31417', soft: '#FCEBEB', strong: '#7A0E10'},
    info: {default: '#3394EF', soft: '#E6F1FB', strong: '#0C447C'},
}

// IMPORTANT: This font stack comes from the FIGMA, not from the codebase.
// The codebase currently uses Barlow + Outfit (see src/utils/font.css).
// The migration guide explains how to swap it.
const typography = {
    fontFamily: {
        sans: `'Inter', system-ui, -apple-system, 'Segoe UI', sans-serif`,
        display: `'Manrope', 'Inter', system-ui, sans-serif`,
        mono: `ui-monospace, 'SF Mono', Menlo, monospace`,
    },
    fontSize: {
        xs: '11px',
        sm: '12px',
        base: '14px',
        md: '15px',
        lg: '16px',
        xl: '18px',
        '2xl': '20px',
        '3xl': '24px',
        '4xl': '32px',
        '5xl': '40px',
        display: '48px',
    },
    fontWeight: {
        light: 300,
        regular: 400,
        medium: 500,
        semibold: 600,
        bold: 700,
    },
    lineHeight: {tight: 1.15, snug: 1.3, normal: 1.5, relaxed: 1.7},
}

const space = {
    0: '0',
    1: '4px',
    2: '8px',
    3: '12px',
    4: '16px',
    5: '20px',
    6: '24px',
    8: '32px',
    10: '40px',
    12: '48px',
    16: '64px',
    20: '80px',
}

const radius = {
    none: '0',
    sm: '4px',
    md: '8px',
    lg: '12px',
    xl: '16px',
    pill: '9999px',
}

const shadow = {
    sm: '0 1px 2px rgba(1, 36, 65, 0.04)',
    md: '0 2px 8px rgba(1, 36, 65, 0.06)',
    lg: '0 8px 24px rgba(1, 36, 65, 0.08)',
    focus: '0 0 0 3px rgba(51, 148, 239, 0.25)',
}

const layout = {
    sidebarWidth: '248px',
    sidebarWidthCollapsed: '64px',
    headerHeight: '64px',
    contentMaxWidth: '1200px',
}

const motion = {
    fast: '120ms',
    base: '200ms',
    slow: '320ms',
    easing: 'cubic-bezier(0.2, 0.8, 0.2, 1)',
}

const zIndex = {
    base: 0,
    sticky: 10,
    overlay: 50,
    modal: 100,
    popover: 150,
    toast: 200,
}

// ============================================================================
// THEME FACTORY — call this at startup with the resolved tenant
// ============================================================================

export function buildTheme(tenant: TenantId = 'default') {
    const brand = resolveBrand(tenant)

    return {
        tenant,
        brand,
        surface,
        text,
        border,
        feedback,
        typography,
        space,
        radius,
        shadow,
        layout,
        motion,
        zIndex,

        // MUI-specific shape — pass to createTheme()
        mui: {
            palette: {
                primary: {
                    main: brand.navy,
                    light: brand.blue,
                    dark: brand.navySoft,
                    contrastText: text.inverse,
                },
                secondary: {
                    main: brand.teal,
                    light: brand.tealSoft,
                    contrastText: brand.navy,
                },
                error: {
                    main: feedback.danger.default,
                    light: feedback.danger.soft,
                    dark: feedback.danger.strong,
                },
                warning: {
                    main: feedback.warning.default,
                    light: feedback.warning.soft,
                    dark: feedback.warning.strong,
                },
                success: {
                    main: feedback.success.default,
                    light: feedback.success.soft,
                    dark: feedback.success.strong,
                },
                info: {
                    main: feedback.info.default,
                    light: feedback.info.soft,
                    dark: feedback.info.strong,
                },
                background: {
                    default: surface.page,
                    paper: surface.card,
                },
                text: {
                    primary: text.primary,
                    secondary: text.secondary,
                    disabled: text.muted,
                },
                divider: border.subtle,
            },
            typography: {
                fontFamily: typography.fontFamily.sans,
                h1: {fontFamily: typography.fontFamily.display, fontWeight: 600, fontSize: typography.fontSize.display},
                h2: {fontFamily: typography.fontFamily.display, fontWeight: 600, fontSize: typography.fontSize['4xl']},
                h3: {fontWeight: 600, fontSize: typography.fontSize['3xl']},
                h4: {fontWeight: 600, fontSize: typography.fontSize.xl},
                body1: {fontSize: typography.fontSize.base, lineHeight: typography.lineHeight.normal},
                body2: {fontSize: typography.fontSize.sm, lineHeight: typography.lineHeight.normal},
                button: {textTransform: 'none' as const, fontWeight: 500},
            },
            shape: {
                borderRadius: 8,
            },
        },
    }
}

export const theme = buildTheme(detectTenant())

// ============================================================================
// TypeScript module augmentation — gives autocomplete on `props.theme.*`
// ============================================================================

declare module 'styled-components' {
    export interface DefaultTheme extends ReturnType<typeof buildTheme> {}
}

// ============================================================================
// USAGE EXAMPLES
// ============================================================================

/* In a styled component:

   import styled from 'styled-components'

   const Card = styled.div`
       background: ${({theme}) => theme.surface.card};
       color: ${({theme}) => theme.text.primary};
       border: 1px solid ${({theme}) => theme.border.subtle};
       border-radius: ${({theme}) => theme.radius.lg};
       padding: ${({theme}) => theme.space[5]};
   `

   const Title = styled.h1`
       font-family: ${({theme}) => theme.typography.fontFamily.display};
       font-size: ${({theme}) => theme.typography.fontSize.display};
       background: ${({theme}) => theme.brand.gradients.headline};
       -webkit-background-clip: text;
       -webkit-text-fill-color: transparent;
   `
*/
